"use client";

import type { SectionMeta } from "@/entities/section";
import type { TeamMember } from "@/entities/team";
import type { ContactChannel } from "@/entities/contacts";
import { useState, useRef, useEffect, useMemo } from "react";
import {
  PageFooterSection,
  type TeamMember as PFTeamMember,
  type ContactLink,
} from "./PageFooterSection";
import Link from "next/link";
import type { Article } from "@/entities/article";
import { GRID_LAYOUTS } from "@/widgets/media-feed";
import { AccentStar } from "@/shared/ui/accent-star";
import mediaPaths from "@/shared/ui/icons/svg-g9h100ep06";
import { Phone, Mail, Send, MessageCircle } from "lucide-react";

/* ── Media animated hero logo (char-appear, same technique as HelpPage HeroSection) ── */
type MediaLogoAnimState = "idle" | "animating" | "done";
const MEDIA_SESSION_KEY = "practicum_media_intro_v1";
const _MS  = 0.13;
const _MWP = 0.25;
const _MMS = 9 * _MS + _MWP; // media section start

function _mcStyle(d: number, s: MediaLogoAnimState): React.CSSProperties {
  if (s === "done") return { opacity: 1 };
  if (s === "idle") return { opacity: 0 };
  return { opacity: 0, animation: `char-appear 0.001s step-start ${d.toFixed(3)}s forwards` };
}

const MEDIA_CHARS: Array<{ d: string; delay: number; clip?: boolean }> = [
  { d: mediaPaths.p37d65f00, delay: 0 * _MS },
  { d: mediaPaths.p26f2d300, delay: 1 * _MS },
  { d: mediaPaths.p2dbc6630, delay: 2 * _MS },
  { d: mediaPaths.pc698300,  delay: 3 * _MS },
  { d: mediaPaths.p3c72e200, delay: 4 * _MS },
  { d: mediaPaths.p38a44d00, delay: 5 * _MS },
  { d: mediaPaths.p16b3f700, delay: 6 * _MS },
  { d: mediaPaths.pa113880,  delay: 7 * _MS },
  { d: mediaPaths.p17fda300, delay: 8 * _MS, clip: true },
  { d: mediaPaths.p2c305600, delay: _MMS },
  { d: mediaPaths.p197a2cc0, delay: _MMS + 1 * _MS },
  { d: mediaPaths.p3a45d80,  delay: _MMS + 2 * _MS },
  { d: mediaPaths.p1dda4900, delay: _MMS + 3 * _MS },
  { d: mediaPaths.pe408680,  delay: _MMS + 4 * _MS },
];
const MEDIA_STAR_DELAY = 8 * _MS + 0.25;

function AnimatedMediaLogo({ state }: { state: MediaLogoAnimState }) {
  const starBase: React.CSSProperties = { transformBox: "fill-box", transformOrigin: "center" };
  const starStyle: React.CSSProperties = state === "done"
    ? { ...starBase, opacity: 1, transform: "scale(1)" }
    : state === "animating"
      ? { ...starBase, opacity: 0, animation: `star-bounce-zoom 0.6s cubic-bezier(0.34,1.56,0.64,1) ${MEDIA_STAR_DELAY.toFixed(3)}s forwards` }
      : { ...starBase, opacity: 0, transform: "scale(0)" };
  return (
    <svg viewBox="0 0 282.876 60.548" fill="none" className="w-full h-full max-w-[min(800px,90vw)]" aria-label="Practicum Media">
      {MEDIA_CHARS.map((c, i) => (
        <path
          key={i}
          d={c.d}
          fill="currentColor"
          clipRule={c.clip ? "evenodd" : undefined}
          fillRule={c.clip ? "evenodd" : undefined}
          style={_mcStyle(c.delay, state)}
        />
      ))}
      <g style={starStyle}>
        <path d={mediaPaths.p18c3f580} fill="#FF5E0E" clipRule="evenodd" fillRule="evenodd" />
      </g>
    </svg>
  );
}

function MediaLogoHero() {
  const [state, setState] = useState<MediaLogoAnimState>("idle");
  const elRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const done = typeof sessionStorage !== "undefined" && sessionStorage.getItem(MEDIA_SESSION_KEY) === "1";
    if (done) { setState("done"); return; }
    const el = elRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        sessionStorage.setItem(MEDIA_SESSION_KEY, "1");
        setState("animating");
        io.disconnect();
      }
    }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={elRef} className="flex items-center justify-center flex-1 min-h-0">
      <AnimatedMediaLogo state={state} />
    </div>
  );
}

interface MediaContentProps {
  articles: Article[];
  section?: SectionMeta;
  teamMembers: TeamMember[];
  contactChannels: ContactChannel[];
  onNavigateToSchool?: () => void;
}

const ICON_BY_KIND: Record<ContactChannel["kind"], React.ReactNode> = {
  phone: <Phone className="w-5 h-5" />,
  email: <Mail className="w-5 h-5" />,
  telegram: <Send className="w-5 h-5" />,
  whatsapp: <MessageCircle className="w-5 h-5" />,
  address: <Mail className="w-5 h-5" />,
};

/* ── Generative SVG abstractions ── */
function CardSvg({ index }: { index: number }) {
  const variant = index % 6;

  return (
    <svg
      viewBox="0 0 200 200"
      className="absolute pointer-events-none fill-media/10 stroke-media/25 group-hover:fill-white/10 group-hover:stroke-white/20"
      style={{
        width: "65%",
        height: "65%",
        right: "-8%",
        bottom: "-8%",
        transition: "opacity 0.2s",
      }}
    >
      {variant === 0 && (
        /* concentric circles */
        <>
          <circle cx="100" cy="100" r="90" fill="none" strokeWidth="1" />
          <circle cx="100" cy="100" r="65" fill="none" strokeWidth="1" />
          <circle cx="100" cy="100" r="40" fill="none" strokeWidth="1" />
          <circle cx="100" cy="100" r="15" />
        </>
      )}
      {variant === 1 && (
        /* diagonal lines */
        <>
          {[0, 28, 56, 84, 112, 140, 168].map((d) => (
            <line key={d} x1={d} y1="0" x2={d + 60} y2="200" strokeWidth="1" />
          ))}
          <rect x="50" y="50" width="100" height="100" rx="50" />
        </>
      )}
      {variant === 2 && (
        /* dot grid */
        <>
          {Array.from({ length: 7 }, (_, r) =>
            Array.from({ length: 7 }, (_, c) => (
              <circle
                key={`${r}-${c}`}
                cx={20 + c * 28}
                cy={20 + r * 28}
                r={r === 3 && c === 3 ? 8 : 3}
                className={r === 3 && c === 3 ? "" : "fill-current"}
              />
            )),
          )}
        </>
      )}
      {variant === 3 && (
        /* nested squares rotated */
        <>
          <g transform="translate(100,100)">
            <rect
              x="-80"
              y="-80"
              width="160"
              height="160"
              fill="none"
              strokeWidth="1"
              transform="rotate(0)"
            />
            <rect
              x="-56"
              y="-56"
              width="112"
              height="112"
              fill="none"
              strokeWidth="1"
              transform="rotate(15)"
            />
            <rect
              x="-36"
              y="-36"
              width="72"
              height="72"
              fill="none"
              strokeWidth="1"
              transform="rotate(30)"
            />
            <rect
              x="-16"
              y="-16"
              width="32"
              height="32"
              transform="rotate(45)"
            />
          </g>
        </>
      )}
      {variant === 4 && (
        /* wave arcs */
        <>
          {[30, 60, 90, 120, 150].map((r) => (
            <path
              key={r}
              d={`M 0 ${200 - r * 0.8} Q 100 ${200 - r * 1.6} 200 ${200 - r * 0.8}`}
              fill="none"
              strokeWidth="1"
            />
          ))}
          <circle cx="100" cy="140" r="20" />
        </>
      )}
      {variant === 5 && (
        /* radial spokes */
        <>
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            return (
              <line
                key={i}
                x1="100"
                y1="100"
                x2={Number((100 + Math.cos(angle) * 90).toFixed(4))}
                y2={Number((100 + Math.sin(angle) * 90).toFixed(4))}
                strokeWidth="1"
              />
            );
          })}
          <circle cx="100" cy="100" r="30" fill="none" strokeWidth="1" />
          <circle cx="100" cy="100" r="8" />
        </>
      )}
    </svg>
  );
}

/* ── Article card with hover inversion ── */
function ArticleCard({
  article,
  layout,
  index,
  onClick,
}: {
  article: Article;
  layout: { colSpan: number; rowSpan: number };
  index: number;
  onClick: () => void;
}) {
  const isLarge = layout.colSpan >= 2 && layout.rowSpan >= 2;
  const isBanner = layout.colSpan === 3;

  return (
    <button
      onClick={onClick}
      className="group w-full h-full text-left rounded-xl border-2 transition-colors duration-200 overflow-hidden relative bg-white lg:group-hover:bg-media border-border lg:group-hover:border-media"
      style={{
        gridColumn: `span ${layout.colSpan}`,
        gridRow: `span ${layout.rowSpan}`,
      }}
    >
      {/* SVG abstraction */}
      <CardSvg index={index} />

      <div
        className={`flex flex-col justify-between h-full relative z-10 ${isLarge ? "p-fluid-md" : "p-fluid-sm"}`}
      >
        {/* Top: category + read time */}
        <div className="flex items-center gap-fluid-sm">
          <span className="text-fluid-xs tracking-[0.15em] uppercase transition-colors duration-200 text-media lg:group-hover:text-white/70">
            {article.category}
          </span>
          <span className="text-fluid-xs transition-colors duration-200 text-muted lg:group-hover:text-white/50">
            {article.readTime}
          </span>
        </div>

        {/* Bottom: title + subtitle */}
        <div className="mt-auto">
          <h3
            className={`tracking-tight mb-fluid-sm transition-colors duration-200 ${
              isLarge
                ? "text-fluid-3xl"
                : isBanner
                  ? "text-fluid-2xl"
                  : "text-fluid-xl"
            } text-foreground lg:group-hover:text-white`}
          >
            {article.title}
          </h3>
          <p
            className={`leading-relaxed transition-colors duration-200 ${
              isLarge
                ? "text-fluid-lg"
                : isBanner
                  ? "text-fluid-sm"
                  : "text-fluid-sm"
            } text-muted lg:group-hover:text-white/70`}
          >
            {article.subtitle}
          </p>
        </div>
      </div>
    </button>
  );
}

export function MediaContent({
  articles,
  teamMembers,
  contactChannels,
  onNavigateToSchool,
}: MediaContentProps) {
  const teamMembersUI: PFTeamMember[] = teamMembers.map((m) => ({
    name: m.name,
    role: m.role,
    initials: m.initials,
    photo: m.photo,
  }));
  const contactLinks: ContactLink[] = contactChannels.map((c) => ({
    icon: ICON_BY_KIND[c.kind],
    title: c.title,
    value: c.value,
    href: c.href,
  }));
  const [filter, setFilter] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(0);


  /* Measure container → derive square cell size (3 columns, gap 16px).
   * TODO(M11): replace JS measurement with CSS `aspect-ratio: 1 / 1` + `cqw`
   * units on grid items (spec §6.4). Kept as ResizeObserver for now —
   * media feed is non-critical, not part of TriadRing geometry, and the
   * CSS migration requires verifying img/SVG layout in each cell. */
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const GAP = 16;
    const measure = () => {
      const w = el.clientWidth;
      setCellSize(Math.floor((w - GAP * 2) / 3));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const categories = useMemo(
    () => ["Все", ...Array.from(new Set(articles.map((a) => a.category)))],
    [articles],
  );

  const filtered = useMemo(
    () =>
      filter && filter !== "Все"
        ? articles.filter((a) => a.category === filter)
        : articles,
    [articles, filter],
  );

  return (
    <div className="flex flex-col gap-fluid-2xl">
      {/* Hero */}
      <section
        className="flex flex-col"
        style={{
          minHeight:
            "clamp(480px, calc(100svh - 2 * (var(--ring-nav-height, 80px) + var(--ring-nav-padding, 12px) * 2)), 100svh)",
        }}
      >
        <div className="flex flex-col gap-fluid-lg h-full p-fluid-xl flex-1 rounded-[var(--fluid-radius-lg)] border border-[var(--media)] bg-white overflow-hidden">
          {/* Логотип — char-appear animation, once per session */}
          <MediaLogoHero />

          {/* overflow-hidden скрывает блок до момента выдвижения */}
          <div className="overflow-hidden shrink-0">
            <div className="text-left pt-fluid-lg border-t border-[var(--media)]/20">
              <h1 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
                Прозрачность <span className="text-media">сложного</span>
              </h1>
              <p className="hidden font-content font-light text-fluid-xl leading-snug text-[var(--foreground)] mt-fluid-sm block select-none max-w-[65ch]">
                Научная обоснованность без упрощения. Делаем сложное понятным.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content section */}
      {/* <div>
        <div className="flex flex-wrap gap-fluid-sm mb-fluid-lg">
          {categories.map((cat) => {
            const isActive = filter === cat || (cat === "Все" && !filter);
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat === "Все" ? null : cat)}
                className="px-5 py-2.5 rounded-full text-fluid-sm tracking-wide transition-colors duration-200 bg-transparent data-[active=true]:bg-media text-foreground data-[active=true]:text-white border-border data-[active=true]:border-media font-content"
                data-active={isActive}
              >
                {cat}
              </button>
            );
          })}
        </div>

        <div
          ref={gridRef}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gridAutoRows: cellSize || 320,
            gridAutoFlow: "dense",
            gap: "var(--fluid-space-sm)",
          }}
        >
          {filtered.map((article, i) => {
            const layout = GRID_LAYOUTS[i % GRID_LAYOUTS.length];
            return (
              <ArticleCard
                key={article.id}
                article={article}
                layout={layout}
                index={i}
                onClick={() => openArticle(article.id)}
              />
            );
          })}
        </div>

        <div className="mt-fluid-xl p-fluid-lg rounded-2xl border-2 border-media">
          <h2 className="text-fluid-3xl tracking-tight mb-fluid-sm font-heading">
            В курсах — <span className="text-media">практика</span>
          </h2>
          <p className="text-fluid-lg mb-fluid-md max-w-2xl font-content text-foreground">
            Упражнения, техники, протоколы. То, что можно применять.
          </p>
          <button
            onClick={onNavigateToSchool}
            className="px-8 py-4 rounded-full text-fluid-base transition-all hover:opacity-90 bg-media text-white font-content"
          >
            Перейти к курсам
          </button>
        </div>
      </div> */}

      <PageFooterSection
        teamMembers={teamMembersUI}
        teamLabel="Команда Practicum Media"
        contactLinks={contactLinks}
        accentColor="var(--media)"
      />
    </div>
  );
}

export function ArticlePage({
  articleId,
  articles,
  onBack,
  onNavigateToSchool,
}: {
  articleId: string;
  articles: Article[];
  onBack?: () => void;
  onNavigateToSchool?: () => void;
}) {
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="w-full" style={{ backgroundColor: "#F9F9F9" }}>
        <div className="relative w-full" style={{ paddingBottom: "63.28%" }}>
          <div className="w-full h-full min-h-[100px] bg-zinc-800 rounded animate-pulse" />
          <div className="w-full h-full min-h-[100px] bg-zinc-800 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 py-16 lg:px-24 lg:py-20">
      <Link
        href="/media"
        onClick={onBack ? (e) => { e.preventDefault(); onBack(); } : undefined}
        className="mb-fluid-md text-fluid-base transition-colors flex items-center gap-fluid-xs font-content text-media"
      >
        <span>←</span> Все статьи
      </Link>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-fluid-xs tracking-[0.15em] uppercase font-content text-media">
            {article.category}
          </span>
          <span className="text-fluid-xs font-content text-muted">
            {article.readTime}
          </span>
        </div>

        <h1 className="text-fluid-4xl tracking-tight mb-fluid-sm font-heading leading-tight">
          {article.title}
        </h1>

        <p className="text-fluid-xl leading-relaxed mb-fluid-lg font-content text-muted">
          {article.subtitle}
        </p>

        <div
          className="w-full rounded-xl overflow-hidden mb-fluid-lg relative bg-media/15"
          style={{ height: 400 }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <AccentStar color="#FF5E0E" size={48} style={{ opacity: 0.2 }} />
          </div>
        </div>

        <div
          className="prose prose-lg max-w-none"
          style={{ fontFamily: "var(--font-content)", color: "#1B1D20" }}
        >
          {article.content.split("\n\n").map((para, i) => (
            <p key={i} className="mb-6 text-lg leading-relaxed">
              {para}
            </p>
          ))}
        </div>

        <div className="mt-fluid-xl p-fluid-md rounded-2xl border-2 border-media">
          <h3 className="text-fluid-2xl tracking-tight mb-fluid-xs font-heading">
            Хотите практики?
          </h3>
          <p className="text-fluid-base mb-fluid-md font-content text-foreground">
            Курсы — от понимания к конкретным техникам.
          </p>
          <Link
            href="/school"
            onClick={onNavigateToSchool ? (e) => { e.preventDefault(); onNavigateToSchool(); } : undefined}
            className="px-6 py-3 rounded-full text-fluid-sm transition-all bg-media text-white font-content"
          >
            К курсам
          </Link>
        </div>
      </div>
    </div>
  );
}
